import { useEffect, useRef, useState } from 'react';
import { subscribeInFlight } from '@/lib/api';

/**
 * GlobalProgressBar — QA UX FIX-20
 *
 * Renders an nprogress-style bar at the very top of the viewport
 * whenever at least one API request is in flight. Subscribes to the
 * in-flight counter exposed by lib/api.ts so every api.get/post/etc
 * automatically feeds this without callers touching anything.
 *
 * Visual behaviour:
 * - On first in-flight request: fade in, animate to 80% over ~4s
 *   (never reaches 100% while requests are still pending — we don't
 *   know the real progress, so we mimic nprogress's trickling curve).
 * - When counter drops to 0: snap to 100%, fade out, then reset.
 * - If another request starts before fade-out finishes, restart the
 *   trickle from wherever we are rather than flashing.
 */
export function GlobalProgressBar(): JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const trickleRef = useRef<number | null>(null);
  const fadeOutRef = useRef<number | null>(null);

  useEffect(() => {
    const clearTrickle = (): void => {
      if (trickleRef.current !== null) {
        window.clearInterval(trickleRef.current);
        trickleRef.current = null;
      }
    };
    const clearFadeOut = (): void => {
      if (fadeOutRef.current !== null) {
        window.clearTimeout(fadeOutRef.current);
        fadeOutRef.current = null;
      }
    };

    const unsubscribe = subscribeInFlight((count) => {
      if (count > 0) {
        // A request is in flight — make sure we're visible and trickling.
        clearFadeOut();
        setVisible(true);
        // If we had already "completed" and were about to hide, restart.
        setProgress((p) => (p >= 100 || p === 0 ? 8 : p));
        if (trickleRef.current === null) {
          trickleRef.current = window.setInterval(() => {
            setProgress((p) => {
              // Trickle towards 80% but never touch it.
              if (p >= 80) return p + 0.2;
              const remaining = 80 - p;
              return p + Math.max(0.5, remaining * 0.08);
            });
          }, 200);
        }
      } else {
        // All requests settled — snap to 100 then fade.
        clearTrickle();
        setProgress(100);
        clearFadeOut();
        fadeOutRef.current = window.setTimeout(() => {
          setVisible(false);
          // reset after the fade transition
          window.setTimeout(() => setProgress(0), 250);
        }, 220);
      }
    });

    return () => {
      unsubscribe();
      clearTrickle();
      clearFadeOut();
    };
  }, []);

  if (!visible && progress === 0) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: 9999,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 220ms ease-out',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${Math.min(progress, 100)}%`,
          background: 'linear-gradient(90deg, #0ea5e9 0%, #22d3ee 100%)',
          boxShadow: '0 0 8px rgba(14, 165, 233, 0.6)',
          transition: 'width 200ms ease-out',
        }}
      />
    </div>
  );
}
